import {
  Building2,
  Users,
  UserRound,
  FileText,
  AlertCircle,
  Clock,
  ChevronRight,
  MoreVertical,
  Pause,
  Play,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'
import type { OrganizationListItem } from '../../hooks/use-organizations'

interface OrganizationTableProps {
  organizations: OrganizationListItem[]
  loading?: boolean
  onSort?: (field: string) => void
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onViewDetails: (id: string) => void
  onSuspend?: (org: OrganizationListItem) => void
  onReactivate?: (org: OrganizationListItem) => void
}

function MetricPill({
  icon: Icon,
  value,
  tone,
  label,
}: {
  icon: LucideIcon
  value: number
  tone: 'blue' | 'purple' | 'teal'
  label: string
}) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 [&_svg]:text-blue-600',
    purple: 'bg-purple-50 text-purple-700 [&_svg]:text-purple-600',
    teal: 'bg-teal-50 text-teal-700 [&_svg]:text-teal-600',
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${tones[tone]}`}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
      {value}
    </span>
  )
}

export function OrganizationTable({
  organizations,
  loading = false,
  onSort,
  sortBy,
  sortOrder,
  onViewDetails,
  onSuspend,
  onReactivate,
}: OrganizationTableProps) {
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  const getDisplayStatus = (org: OrganizationListItem) =>
    org.account_status === 'suspended' ? 'suspended' : org.subscription_status

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; bgColor: string; textColor: string; dotColor: string }> = {
      trial: { label: 'Trial', bgColor: 'bg-amber-50', textColor: 'text-amber-700', dotColor: 'bg-amber-500' },
      active: { label: 'Ativo', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700', dotColor: 'bg-emerald-500' },
      past_due: { label: 'Atrasado', bgColor: 'bg-red-50', textColor: 'text-red-700', dotColor: 'bg-red-500' },
      suspended: { label: 'Suspenso', bgColor: 'bg-gray-100', textColor: 'text-gray-600', dotColor: 'bg-gray-400' },
      churned: { label: 'Churned', bgColor: 'bg-red-50', textColor: 'text-red-700', dotColor: 'bg-red-500' },
      canceled: { label: 'Cancelado', bgColor: 'bg-gray-100', textColor: 'text-gray-600', dotColor: 'bg-gray-400' },
      none: { label: 'Sem plano', bgColor: 'bg-slate-100', textColor: 'text-slate-600', dotColor: 'bg-slate-400' },
    }
    const badge = badges[status] || { label: status, bgColor: 'bg-gray-100', textColor: 'text-gray-600', dotColor: 'bg-gray-400' }
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${badge.bgColor} ${badge.textColor}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${badge.dotColor}`} />
        {badge.label}
      </span>
    )
  }

  const getEngagementColor = (score: number) => {
    if (score >= 70) return 'text-emerald-600'
    if (score >= 40) return 'text-amber-600'
    return 'text-red-500'
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const SortHeader = ({
    field,
    children,
    className = '',
  }: {
    field: string
    children: React.ReactNode
    className?: string
  }) => (
    <th
      onClick={() => onSort?.(field)}
      className={`px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100/80 transition-colors ${className} ${
        sortBy === field ? 'bg-gray-100/60 text-gray-700' : ''
      }`}
    >
      <div className="flex items-center gap-1.5">
        {children}
        {sortBy === field && (
          <span className="text-teal-600 text-sm">{sortOrder === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  )

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 border-b" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 border-b" />
          ))}
        </div>
      </div>
    )
  }

  if (organizations.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Nenhuma organização encontrada</p>
        <p className="text-sm text-gray-400 mt-1">Tente ajustar os filtros de busca</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible">
      <div className="divide-y divide-gray-100 md:hidden">
        {organizations.map((org) => (
          <div
            key={org.id}
            className="cursor-pointer p-4 transition-colors hover:bg-gray-50"
            onClick={() => onViewDetails(org.id)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100">
                  <Building2 className="h-4 w-4 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{org.name}</p>
                  <p className="mt-0.5 truncate text-xs text-gray-500">{org.slug}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {getStatusBadge(getDisplayStatus(org))}
                    {org.alerts.critical > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                        <AlertCircle className="h-3 w-3" />
                        {org.alerts.critical} crítico{org.alerts.critical > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="relative shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenMenu(openMenu === org.id ? null : org.id)
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {openMenu === org.id && (
                  <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setOpenMenu(null)} />
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]">
                      {org.account_status !== 'suspended' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onSuspend?.(org)
                            setOpenMenu(null)
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Pause className="w-4 h-4" />
                          Suspender
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onReactivate?.(org)
                            setOpenMenu(null)
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                        >
                          <Play className="w-4 h-4" />
                          Reativar
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Métricas</p>
                <div className="mt-2 flex items-center gap-2">
                  <MetricPill icon={Users} value={org.metrics.active_members} tone="blue" label="Usuários" />
                  <MetricPill icon={UserRound} value={org.metrics.active_patients} tone="purple" label="Pacientes" />
                  <MetricPill icon={FileText} value={org.metrics.reports_30d} tone="teal" label="Relatórios" />
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Engajamento</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-sm font-semibold ${getEngagementColor(org.metrics.engagement_score)}`}>
                    {org.metrics.engagement_score}%
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full rounded-full ${
                        org.metrics.engagement_score >= 70
                          ? 'bg-emerald-500'
                          : org.metrics.engagement_score >= 40
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.max(org.metrics.engagement_score, 5)}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Trial / criação</p>
                <p className="mt-1 text-sm text-gray-700">
                  {org.subscription_status === 'trial' && org.trial_days_remaining !== null
                    ? `${org.trial_days_remaining}d restantes`
                    : 'Sem trial ativo'}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">Criado em {formatDate(org.created_at)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto overflow-y-visible md:block">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50/80 border-b border-gray-200">
            <tr>
              <SortHeader field="name">Organização</SortHeader>
              <SortHeader field="status">Status</SortHeader>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Métricas
              </th>
              <SortHeader field="engagement_score">Engajamento</SortHeader>
              <SortHeader field="trial_ends_at">Trial</SortHeader>
              <SortHeader field="created_at" className="hidden xl:table-cell">Criado em</SortHeader>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {organizations.map((org) => (
              <tr
                key={org.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => onViewDetails(org.id)}
              >
                {/* Organization Info */}
                <td className="px-3 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 shrink-0 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-900">{org.name}</p>
                      <p className="truncate text-sm text-gray-500">{org.slug}</p>
                    </div>
                  </div>
                </td>

                {/* Status */}
                <td className="px-3 py-4">
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(getDisplayStatus(org))}
                    {org.alerts.critical > 0 && (
                      <div className="flex items-center gap-1 text-xs text-red-600">
                        <AlertCircle className="w-3 h-3" />
                        {org.alerts.critical} crítico{org.alerts.critical > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </td>

                {/* Metrics */}
                <td className="px-3 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-md" title="Usuários">
                      <Users className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">{org.metrics.active_members}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded-md" title="Pacientes">
                      <UserRound className="w-3.5 h-3.5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-700">{org.metrics.active_patients}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-teal-50 rounded-md" title="Relatórios (30d)">
                      <FileText className="w-3.5 h-3.5 text-teal-600" />
                      <span className="text-sm font-medium text-teal-700">{org.metrics.reports_30d}</span>
                    </div>
                  </div>
                </td>

                {/* Engagement */}
                <td className="px-3 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className={`w-4 h-4 ${getEngagementColor(org.metrics.engagement_score)}`} />
                      <span className={`text-sm font-semibold ${getEngagementColor(org.metrics.engagement_score)}`}>
                        {org.metrics.engagement_score}%
                      </span>
                    </div>
                    <div className="hidden h-2 w-16 overflow-hidden rounded-full bg-gray-100 lg:block">
                      <div
                        className={`h-full rounded-full transition-all ${
                          org.metrics.engagement_score >= 70
                            ? 'bg-emerald-500'
                            : org.metrics.engagement_score >= 40
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.max(org.metrics.engagement_score, 5)}%` }}
                      />
                    </div>
                  </div>
                </td>

                {/* Trial */}
                <td className="px-3 py-4">
                  {org.subscription_status === 'trial' && org.trial_days_remaining !== null ? (
                    <div className="flex items-center gap-1 text-sm">
                      <Clock
                        className={`w-4 h-4 ${
                          org.trial_days_remaining <= 3 ? 'text-red-500' : 'text-amber-500'
                        }`}
                      />
                      <span
                        className={
                          org.trial_days_remaining <= 3 ? 'text-red-600 font-medium' : 'text-gray-600'
                        }
                      >
                        {org.trial_days_remaining}d restantes
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>

                {/* Created At */}
                <td className="hidden xl:table-cell px-3 py-4 text-sm text-gray-500">{formatDate(org.created_at)}</td>

                {/* Actions */}
                <td className="px-3 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewDetails(org.id)
                      }}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Ver detalhes"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenMenu(openMenu === org.id ? null : org.id)
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {openMenu === org.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]">
                          {org.account_status !== 'suspended' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onSuspend?.(org)
                                setOpenMenu(null)
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Pause className="w-4 h-4" />
                              Suspender
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onReactivate?.(org)
                                setOpenMenu(null)
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                            >
                              <Play className="w-4 h-4" />
                              Reativar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
